import React from 'react';
import PropTypes from 'prop-types';

import {Box, IconButton} from 'views/components';
import {CloseRounded, SearchRounded} from 'views/icons';
import {translate} from 'views/module';

const Search = (props) => {
  const {
    selection,
    filterOptions,
    updateFilter,
  } = props;

  const searchFilter = filterOptions?.searchString;
  const selectedSearchValue = selection?.filter?.searchString?.optionSelected ?? '';

  const [isExpanded, setIsExpanded] = React.useState(Boolean(selectedSearchValue));
  const [searchText, setSearchText] = React.useState(selectedSearchValue);

  const searchInputRef = React.useRef(null);

  React.useEffect(() => {
    setSearchText(selectedSearchValue);

    if (selectedSearchValue) {
      setIsExpanded(true);
    }
  }, [selectedSearchValue]);

  if (!searchFilter) {
    return null;
  }

  const handleChange = (event) => {
    const value = event.target.value;

    setSearchText(value);

    if (value.trim()) {
      updateFilter(searchFilter, {
        key: value,
        label: value,
      });
    } else {
      updateFilter(searchFilter, null);
    }
  };

  const handleBlur = () => {
    if (!searchText.trim()) {
      setIsExpanded(false);
    }
  };

  const handleClearSearch = () => {
    setIsExpanded(true);
    setSearchText('');
    updateFilter(searchFilter, null);
    // Re-focus the search box when clearing so it does not collapse and we can immediately type
    searchInputRef.current?.focus();
  };

  return (
    <Box
      onClick={() => setIsExpanded(true)}
      sx={{
        boxSizing: 'border-box',
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        width: isExpanded ? '246px' : '39px',
        height: '24px',
        px: '9px',
        borderRadius: '20px',
        backgroundColor: '#F0F0F0',
        transition: 'width 0.2s ease',
        cursor: 'text',
        overflow: 'hidden',
        flexShrink: 0,
      }}
    >
      <SearchRounded
        sx={{
          fontSize: '16px',
          color: '#7C7C7C',
          flexShrink: 0,
        }}
      />
      {
        isExpanded &&
        <>
          <Box
            component='input'
            ref={searchInputRef}
            autoFocus
            value={searchText}
            placeholder={translate('clientLead.ClientLeadList.Filter.SearchPlaceholder')}
            onChange={handleChange}
            onBlur={handleBlur}
            sx={{
              'width': '100%',
              'minWidth': 0,
              'p': 0,
              'border': 'none',
              'outline': 'none',
              'backgroundColor': 'transparent',
              'fontFamily': 'Geist Mono',
              'fontStyle': 'normal',
              'fontWeight': 400,
              'fontSize': '11px',
              'lineHeight': '14px',
              'color': '#6B6760',
              '&::placeholder': {
                'color': '#7C7C7C',
                'opacity': 1,
              },
            }}
          />
          <IconButton
            size='small'
            onMouseDown={(event) => event.preventDefault()}
            onClick={handleClearSearch}
            sx={{
              p: 0,
              color: '#7C7C7C',
              visibility: searchText ? 'visible' : 'hidden',
            }}
          >
            <CloseRounded
              sx={{
                fontSize: '14px',
              }}
            />
          </IconButton>
        </>
      }
    </Box>
  );
};

Search.propTypes = {
  selection: PropTypes.object.isRequired,
  filterOptions: PropTypes.object.isRequired,
  updateFilter: PropTypes.func.isRequired,
};

export default React.memo(Search);
